import { NavLink, Outlet, useSearchParams } from "react-router-dom"
import { cn } from "@/lib/utils"
import { VerticalProvider } from "@/contexts/VerticalContext"
import { VerticalFilter } from "@/components/VerticalFilter"
import { LogoutButton } from "@/components/PasswordGate"
import {
  LayoutDashboard,
  Users,
  MapPin,
  Radio,
  Rss,
  Search,
  Activity,
  Upload,
  DollarSign,
  Layers,
  RotateCcw,
  Send,
} from "lucide-react"

const navItems = [
  { path: "/", label: "Overview", icon: LayoutDashboard },
  { path: "/pnl", label: "P&L", icon: DollarSign },
  { path: "/buyers", label: "Buyers", icon: Users },
  { path: "/geography", label: "Geography", icon: MapPin },
  { path: "/publishers", label: "Publishers", icon: Radio },
  { path: "/api-publishers", label: "API Pubs", icon: Rss },
  { path: "/leads", label: "Leads", icon: Search },
  { path: "/stack", label: "Stack", icon: Layers },
  { path: "/ispeed-import", label: "iSpeed Import", icon: Upload },
  { path: "/motivatedleads-refunds", label: "ML Refunds", icon: RotateCcw },
  { path: "/realestatebees-refunds", label: "REB Refunds", icon: RotateCcw },
  { path: "/uvx-leads", label: "UVX", icon: Send },
]

export function Layout() {
  const [searchParams] = useSearchParams()

  // Persist date range params across page navigations
  const dateParams = new URLSearchParams()
  const from = searchParams.get("from")
  const to = searchParams.get("to")
  if (from) dateParams.set("from", from)
  if (to) dateParams.set("to", to)
  const dateSearch = dateParams.toString()

  return (
    <VerticalProvider>
    <div className="min-h-screen bg-background">
      {/* Skip to content link for accessibility */}
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>

      {/* Top Bar */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-12 items-center px-4">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary shadow-glow-gold">
              <Activity className="h-3.5 w-3.5 text-primary-foreground" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-semibold tracking-tight leading-none">
                UVX<span className="text-primary">.</span>Analytics
              </span>
              <span className="text-2xs text-muted-foreground leading-none mt-0.5">
                Lead Performance Dashboard
              </span>
            </div>
          </div>

          {/* Nav */}
          <nav className="ml-8 flex items-center gap-0.5" role="navigation" aria-label="Main navigation">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={dateSearch ? `${item.path}?${dateSearch}` : item.path}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-all",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-background",
                    isActive
                      ? "bg-primary/10 text-primary shadow-sm"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  )
                }
              >
                <item.icon className="h-3.5 w-3.5" aria-hidden="true" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Right side - Status */}
          <div className="ml-auto flex items-center gap-3">
            <VerticalFilter />
            <div className="h-4 w-px bg-border" aria-hidden="true" />
            <div className="flex items-center gap-1.5" role="status" aria-live="polite">
              <div className="status-dot status-dot-success animate-pulse" aria-hidden="true" />
              <span className="text-2xs text-muted-foreground">Live</span>
            </div>
            <div className="h-4 w-px bg-border" aria-hidden="true" />
            <time
              className="font-mono text-2xs text-muted-foreground tabular-nums"
              dateTime={new Date().toISOString()}
            >
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                month: "short",
                day: "numeric",
                timeZone: "America/New_York",
              })}{" "}
              <span className="text-foreground/70">
                {new Date().toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  timeZone: "America/New_York",
                })}
              </span>
              <span className="ml-1 text-muted-foreground">ET</span>
            </time>
            <div className="h-4 w-px bg-border" aria-hidden="true" />
            <LogoutButton />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main id="main-content" className="p-4" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
    </VerticalProvider>
  )
}
